import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { X, ArrowUp, ArrowDown, Plus, Upload, ImagePlus } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

const MAX_IMAGES = 10;
const MAX_BYTES = 8 * 1024 * 1024;
const ALLOWED_MIME = [
    "image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif",
    "image/bmp", "image/tiff", "image/heic", "image/heif", "image/avif",
];
const ALLOWED_EXTS = ["jpg", "jpeg", "png", "webp", "gif", "bmp", "tiff", "tif", "heic", "heif", "avif"];
const ACCEPT_ATTR = "image/jpeg,image/png,image/webp,image/gif,image/bmp,image/tiff,image/heic,image/heif,image/avif";

function extOf(name) {
    return String(name || "").split(".").pop().toLowerCase();
}

export function ProductImageEditor({ images, onChange, testId = "product-image-editor" }) {
    const list = Array.isArray(images) ? images : [];
    const fileRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState("");
    const [urlOpen, setUrlOpen] = useState(false);
    const [urlValue, setUrlValue] = useState("");
    const [dragOver, setDragOver] = useState(false);

    const remaining = MAX_IMAGES - list.length;
    const full = remaining <= 0;
    const setList = (next) => onChange(next);

    const uploadFiles = useCallback(async (rawFiles) => {
        const arr = Array.from(rawFiles || []);
        if (!arr.length) return;
        if (full) {
            toast.error(`Maximum ${MAX_IMAGES} images per product`);
            return;
        }
        // Client-side validation. iOS Safari may hand us an empty `type` for HEIC —
        // fall back to the extension in that case.
        const valid = [];
        for (const f of arr) {
            const ext = extOf(f.name);
            const mime = (f.type || "").toLowerCase();
            if (ext === "svg" || mime === "image/svg+xml") {
                toast.error(`${f.name || "file"}: SVG not supported`);
                continue;
            }
            const mimeOk = mime ? ALLOWED_MIME.includes(mime) || mime.startsWith("image/") : false;
            const extOk = ALLOWED_EXTS.includes(ext);
            if (!mimeOk && !extOk) {
                toast.error(`${f.name || "file"}: format not supported`);
                continue;
            }
            if (f.size > MAX_BYTES) {
                toast.error(`${f.name || "file"}: ${(f.size / 1024 / 1024).toFixed(1)} MB (limit 8 MB)`);
                continue;
            }
            valid.push(f);
        }
        if (!valid.length) return;
        const accepted = valid.slice(0, remaining);
        if (valid.length > remaining) toast.message(`Only the first ${remaining} file(s) will be uploaded — limit is ${MAX_IMAGES}.`);

        setBusy(true);
        setProgress(`Uploading ${accepted.length} of ${accepted.length}…`);
        const fd = new FormData();
        accepted.forEach((f) => fd.append("files", f, f.name || "image"));
        try {
            // CRITICAL: the axios instance defaults to Content-Type: application/json.
            // For multipart uploads the browser MUST set the header itself so it can
            // add the `boundary=...` parameter. Force axios to drop the JSON default
            // by explicitly setting undefined; axios then sniffs FormData and uses
            // the correct multipart/form-data; boundary=… header.
            const { data } = await api.post("/admin/upload", fd, {
                headers: { "Content-Type": undefined },
                onUploadProgress: (e) => {
                    if (!e.total) return;
                    const pct = Math.min(99, Math.floor((e.loaded / e.total) * 100));
                    setProgress(`Uploading ${accepted.length} image${accepted.length === 1 ? "" : "s"}… ${pct}%`);
                },
            });
            const urls = (data.files || []).map((x) => x.url);
            setList([...list, ...urls]);
            toast.success(`Uploaded ${urls.length} image${urls.length === 1 ? "" : "s"}`);
        } catch (e) {
            console.error("Image upload failed", {
                status: e?.response?.status,
                data: e?.response?.data,
                message: e?.message,
                error: e,
            });
            const detail = formatApiErrorDetail(e.response?.data?.detail);
            toast.error(detail || e?.message || "Upload failed");
        } finally {
            setBusy(false);
            setProgress("");
        }
    }, [list, full, remaining, onChange]);

    // Programmatic open of native file picker. Used by both the dropzone tap
    // AND the explicit "Choose Photos" button below (better mobile UX).
    const openPicker = useCallback(() => {
        if (busy || full) return;
        // Some mobile browsers require the click to be inside the same task as
        // the user gesture. Guard against a null ref.
        fileRef.current?.click();
    }, [busy, full]);

    const onFileInputChange = useCallback((e) => {
        const files = Array.from(e.target.files || []);
        // Reset immediately so re-picking the SAME file re-fires change (iOS).
        e.target.value = "";
        if (files.length > 0) uploadFiles(files);
    }, [uploadFiles]);

    const removeAt = async (idx) => {
        const url = list[idx];
        const next = list.filter((_, i) => i !== idx);
        setList(next);
        // If it's one of our uploads, delete from disk. Allow all persisted extensions.
        const m = /^\/api\/uploads\/([a-f0-9]{32}\.(?:jpg|jpeg|png|webp|gif))$/i.exec(url || "");
        if (m) {
            try { await api.delete(`/admin/upload/${m[1]}`); } catch (err) { console.warn("delete failed", err); }
        }
    };

    const move = (idx, delta) => {
        const j = idx + delta;
        if (j < 0 || j >= list.length) return;
        const next = [...list];
        [next[idx], next[j]] = [next[j], next[idx]];
        setList(next);
    };

    const addUrl = () => {
        const v = urlValue.trim();
        if (!v) return;
        if (full) { toast.error(`Maximum ${MAX_IMAGES} images per product`); return; }
        if (!/^https?:\/\//i.test(v) && !v.startsWith("/api/uploads/")) {
            toast.error("Enter a full http(s):// URL or an /api/uploads/ path");
            return;
        }
        setList([...list, v]);
        setUrlValue("");
        setUrlOpen(false);
    };

    return (
        <div data-testid={testId} className="space-y-3">
            {/* Native file input — kept mounted at the component root so its onChange
                is never unmounted mid-picker (some mobile browsers lose the change
                event if the input's ancestor re-renders while the picker is open). */}
            <input
                ref={fileRef}
                type="file"
                multiple
                accept={ACCEPT_ATTR}
                className="sr-only"
                onChange={onFileInputChange}
                data-testid="product-image-file-input"
            />

            <div
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openPicker(); } }}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); if (!busy && !full) uploadFiles(e.dataTransfer.files); }}
                onClick={openPicker}
                className={
                    "relative rounded-2xl border-2 border-dashed px-6 py-8 text-center transition-colors " +
                    (full
                        ? "border-inkDark/10 bg-beige/40 text-inkMuted cursor-not-allowed"
                        : dragOver
                            ? "border-skyblueDeep bg-skyblue/15 cursor-pointer"
                            : "border-inkDark/15 hover:border-skyblueDeep hover:bg-skyblue/5 cursor-pointer")
                }
                data-testid="product-image-dropzone"
            >
                <Upload size={20} className="mx-auto mb-2 text-skyblueDeep" aria-hidden="true" />
                <div className="text-sm font-medium">
                    {full
                        ? `Maximum ${MAX_IMAGES} images per product`
                        : busy
                            ? (progress || "Uploading…")
                            : "Drop images here or tap to upload"}
                </div>
                <div className="text-xs text-inkMuted mt-1">
                    JPG · PNG · WEBP · GIF · BMP · TIFF · HEIC · AVIF — up to 8 MB each — up to {MAX_IMAGES} images per product — {list.length}/{MAX_IMAGES} attached
                </div>
            </div>

            {/* Mobile-friendly explicit button. Dashed drop zones are not tap-obvious on phones. */}
            <button
                type="button"
                onClick={openPicker}
                disabled={busy || full}
                className="inline-flex items-center gap-2 rounded-full bg-skyblueDeep text-white text-sm px-5 py-2.5 shadow-soft disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="product-image-choose-btn"
            >
                <ImagePlus size={16} />
                {busy ? (progress || "Uploading…") : "Choose photos"}
            </button>

            {list.length > 0 ? (
                <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="product-image-thumbs">
                    {list.map((url, idx) => (
                        <li key={`${url}-${idx}`} className="group relative rounded-xl overflow-hidden bg-beige border border-inkDark/5" data-testid={`product-image-thumb-${idx}`}>
                            <div className="aspect-[4/5] w-full">
                                <img src={url} alt={`product image ${idx + 1}`} className="w-full h-full object-cover" />
                            </div>
                            {/* On mobile: always-visible controls; on desktop: hover only. */}
                            <div className="absolute top-1 right-1 flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                                <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} className="bg-ivory/95 rounded-full p-1 shadow-soft disabled:opacity-30" data-testid={`product-image-up-${idx}`} title="Move up"><ArrowUp size={12} /></button>
                                <button type="button" onClick={() => move(idx, 1)} disabled={idx === list.length - 1} className="bg-ivory/95 rounded-full p-1 shadow-soft disabled:opacity-30" data-testid={`product-image-down-${idx}`} title="Move down"><ArrowDown size={12} /></button>
                                <button type="button" onClick={() => removeAt(idx)} className="bg-red-600 text-white rounded-full p-1 shadow-soft" data-testid={`product-image-remove-${idx}`} title="Remove"><X size={12} /></button>
                            </div>
                            <div className="absolute bottom-1 left-1 label-eyebrow text-[9px] bg-ivory/95 px-1.5 py-0.5 rounded">#{idx + 1}</div>
                        </li>
                    ))}
                </ul>
            ) : null}

            <div>
                {urlOpen ? (
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                        <input
                            value={urlValue}
                            onChange={(e) => setUrlValue(e.target.value)}
                            placeholder="https://… or /api/uploads/abc.jpg"
                            className="flex-1 rounded-xl border border-inkDark/10 bg-white px-4 py-2 text-sm"
                            data-testid="product-image-url-input"
                        />
                        <button type="button" onClick={addUrl} className="btn-pill text-xs" data-testid="product-image-url-add">Add</button>
                        <button type="button" onClick={() => { setUrlOpen(false); setUrlValue(""); }} className="text-xs text-inkMuted underline">Cancel</button>
                    </div>
                ) : (
                    <button type="button" onClick={() => setUrlOpen(true)} className="inline-flex items-center gap-1.5 text-xs text-skyblueDeep hover:underline" data-testid="product-image-url-toggle">
                        <Plus size={12} /> Add by URL instead
                    </button>
                )}
            </div>
        </div>
    );
}
